import { useEffect, useState } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, Banknote, CheckCircle2, Upload, Wallet } from "lucide-react";

/**
 * PartnerFundingPage (finbpm-flow-005/006) — the hybrid funding page:
 * FinBPM-issued instructions (settings-driven) shown only after
 * countersignature, with the custody disclaimers, plus "I Have Sent Funds"
 * proof-of-payment upload. FinBPM may be named on this surface (recorded
 * naming exception).
 */
const Field = ({ label, value }) => (
  <p className="text-sm text-slate-700">
    {label}: <span className="font-mono font-semibold text-slate-900 break-all">{value}</span>
  </p>
);

const PartnerFundingPage = () => {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [txHash, setTxHash] = useState("");
  const [amountSent, setAmountSent] = useState("");
  const [dateSent, setDateSent] = useState("");
  const [source, setSource] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoadError(null);
    try {
      const r = await api.get("/finbpm/funding/me");
      setData(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setLoadError((typeof detail === "string" && detail) || "Could not load funding instructions.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveElection = async (method, asset) => {
    try {
      await api.post("/finbpm/funding/election", {
        funding_method: method, funding_asset: asset,
      });
      toast.success("Funding method updated");
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "string" && detail) || "Could not update funding method");
    }
  };

  const upload = async () => {
    setSubmitting(true);
    try {
      const form = new FormData();
      if (file) form.append("file", file);
      if (txHash) form.append("tx_hash", txHash);
      if (amountSent) form.append("amount_sent", amountSent);
      if (dateSent) form.append("date_sent", dateSent);
      if (source) form.append("source", source);
      // No manual Content-Type: the browser sets the multipart boundary.
      await api.post("/finbpm/funding/proof", form);
      toast.success("Proof of payment uploaded — our team will review and confirm receipt.");
      setFile(null); setTxHash(""); setAmountSent(""); setDateSent(""); setSource("");
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "string" && detail) || "Could not upload the proof");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Funding Instructions" subtitle="Loading...">
        <div className="animate-pulse h-32 bg-slate-100 rounded" />
      </DashboardLayout>
    );
  }
  if (loadError || !data) {
    return (
      <DashboardLayout title="Funding Instructions" subtitle="">
        <Card className="bg-white border-slate-200" data-testid="funding-load-error">
          <CardContent className="p-6 flex items-center gap-3">
            <AlertTriangle className="text-amber-600 shrink-0" size={20} />
            <p className="text-sm text-slate-800 flex-1">{loadError || "Could not load funding instructions."}</p>
            <Button variant="outline" onClick={load} className="border-slate-300">Retry</Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Funding Instructions" subtitle="Issued by FinBPM — funds go directly to FinBPM">
      <div className="mb-6 space-y-2" data-testid="funding-disclaimers">
        {data.disclaimers.map((d, i) => (
          <div key={`disc-${i}`} className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            {d}
          </div>
        ))}
      </div>

      <Card className="mb-6 bg-white border-slate-200">
        <CardContent className="p-4 text-sm text-slate-700">
          Approved participation amount:{" "}
          <span className="font-semibold">${Number(data.approved_amount).toLocaleString()}</span>
          {data.pilot_label && <span className="block mt-1 text-amber-700 text-xs">{data.pilot_label}</span>}
        </CardContent>
      </Card>

      {/* funding-instructions-001: election editable until proof uploaded;
          changes after the email flag the admin for a re-send. */}
      {!data.proof_uploaded_at && (
        <Card className="mb-6 bg-white border-slate-200" data-testid="funding-election">
          <CardContent className="p-4 text-sm text-slate-700 flex flex-wrap items-center gap-4">
            <span className="font-semibold">How will you fund?</span>
            <label className="flex items-center gap-2 select-none">
              <input type="radio" name="pf-method" className="accent-slate-900"
                checked={data.funding_method === "wire"}
                onChange={() => saveElection("wire", null)} />
              Bank wire (SSB)
            </label>
            <label className="flex items-center gap-2 select-none">
              <input type="radio" name="pf-method" className="accent-slate-900"
                checked={data.funding_method === "crypto"}
                onChange={() => saveElection("crypto", data.funding_asset || "usdt_trc20")} />
              Crypto
            </label>
            {data.funding_method === "crypto" && (
              <select value={data.funding_asset || ""}
                onChange={(e) => saveElection("crypto", e.target.value)}
                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900"
                data-testid="funding-election-asset">
                <option value="usdt_trc20">USDT — TRC-20 (Tron)</option>
                <option value="usdt_erc20">USDT — ERC-20 (Ethereum)</option>
                <option value="usdc_erc20">USDC — ERC-20 (Ethereum)</option>
              </select>
            )}
          </CardContent>
        </Card>
      )}

      {data.instructions_ready && data.instructions_email?.at && (
        <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900"
          data-testid="funding-emailed-banner">
          The official instructions were <strong>emailed to you on{" "}
          {new Date(data.instructions_email.at).toLocaleString()}</strong>.
          Below is a copy of those instructions for your records.
        </div>
      )}

      {/* STARSEED-CAPMARKETS-064: drift guard — the displayed details no
          longer match the ones this member was emailed. Hard warning; the
          email remains the official channel. */}
      {data.instructions_ready && data.wire_provenance?.drift && (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 font-semibold"
          data-testid="funding-drift-warning">
          The bank wire details have changed since your instructions email was
          sent — the details in that email are no longer valid. Do NOT send
          funds using the emailed details; contact the team and verify the
          correct details directly with FinBPM before wiring.
        </div>
      )}

      {!data.instructions_ready ? (
        <Card className="mb-6 bg-white border-slate-200" data-testid="funding-pending">
          <CardContent className="p-6 text-sm text-slate-700">
            FinBPM's official funding instructions will be <strong>emailed to
            you</strong> once the partner countersigns and the team issues
            them. Do not send funds before you receive that email.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {(data.crypto_wallets || []).length > 0 && (
            <Card className="bg-white border-slate-200" data-testid="funding-crypto-wallets">
              <CardHeader>
                <CardTitle className="text-slate-900">Crypto wallets</CardTitle>
                <CardDescription className="text-slate-600">
                  Send ONLY on the listed network for each address — wrong-network
                  transfers are unrecoverable.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {(data.crypto_wallets || []).map((w) => (
                  <div key={w.asset} className="rounded-md border border-slate-200 p-3">
                    <p className="text-sm font-semibold text-slate-900">{w.coin} · {w.network}</p>
                    <p className="text-xs font-mono text-slate-700 break-all mt-1">{w.address}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {data.crypto && (data.crypto_wallets || []).length === 0 && (
            <Card className="bg-white border-slate-200" data-testid="funding-crypto">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Wallet size={18} /> Option A — Crypto
                </CardTitle>
                <CardDescription className="text-slate-600">FinBPM-provided wallet (static unless FinBPM updates it)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Field label="Wallet address" value={data.crypto.wallet} />
                {data.crypto.network && <Field label="Network" value={data.crypto.network} />}
              </CardContent>
            </Card>
          )}
          {data.wire && (
            <Card className="bg-white border-slate-200" data-testid="funding-wire">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Banknote size={18} /> Option B — U.S. bank wire
                </CardTitle>
                <CardDescription className="text-slate-600">SSB Bank / FinBPM wire instructions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.wire.beneficiary && <Field label="Beneficiary" value={data.wire.beneficiary} />}
                {data.wire.bank_name && <Field label="Bank" value={data.wire.bank_name} />}
                {data.wire.bank_address && <Field label="Bank address" value={data.wire.bank_address} />}
                {data.wire.account_number && <Field label="Account number" value={data.wire.account_number} />}
                {data.wire.swift && <Field label="SWIFT / BIC" value={data.wire.swift} />}
                {data.wire.routing && <Field label="Routing (ABA/Fedwire)" value={data.wire.routing} />}
                {/* STARSEED-CAPMARKETS-064: provenance + out-of-band prompt */}
                <div className="pt-2 mt-2 border-t border-slate-200 space-y-1" data-testid="funding-wire-provenance">
                  <p className="text-xs text-slate-500">
                    Issued by FinBPM
                    {data.wire_provenance?.approved_at &&
                      <> · details last approved {new Date(data.wire_provenance.approved_at).toLocaleDateString()}</>}
                  </p>
                  <p className="text-xs text-amber-700">
                    Before your first wire, verify these details directly with
                    FinBPM through a contact channel you already know.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {data.proofs?.length > 0 && (
        <Card className="mb-6 bg-emerald-50 border-emerald-200" data-testid="funding-proofs">
          <CardContent className="p-4 space-y-1">
            {data.proofs.map((p, i) => (
              <p key={`proof-${i}`} className="text-sm text-emerald-900 flex items-center gap-2">
                <CheckCircle2 size={16} className="text-emerald-600" />
                Proof received {p.uploaded_at && new Date(p.uploaded_at).toLocaleString()}
                {p.filename ? ` · ${p.filename}` : ""}{p.tx_hash ? ` · tx ${p.tx_hash.slice(0, 18)}…` : ""}
              </p>
            ))}
            {data.funds_confirmed_at
              ? <p className="text-sm font-semibold text-emerald-900">Funds confirmed by the partner.</p>
              : <p className="text-xs text-emerald-800">Awaiting the partner's receipt confirmation.</p>}
          </CardContent>
        </Card>
      )}

      {data.instructions_ready && !data.funds_confirmed_at && (
        <Card className="bg-white border-slate-200" data-testid="funding-upload-card">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Upload size={18} /> I Have Sent Funds
            </CardTitle>
            <CardDescription className="text-slate-600">
              Upload your wire receipt or payment screenshot, and/or provide the crypto transaction hash.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-slate-900">Receipt (PDF, PNG, or JPEG — 8 MB max)</Label>
              <Input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 bg-white text-slate-900 border-slate-300"
                data-testid="proof-file"
              />
            </div>
            <div>
              <Label className="text-slate-900">Crypto transaction hash (if applicable)</Label>
              <Input value={txHash} onChange={(e) => setTxHash(e.target.value)}
                className="mt-1 bg-white text-slate-900 border-slate-300" data-testid="proof-txhash" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-slate-900">Amount sent (USD)</Label>
                <Input type="number" value={amountSent} onChange={(e) => setAmountSent(e.target.value)}
                  className="mt-1 bg-white text-slate-900 border-slate-300" data-testid="proof-amount" />
              </div>
              <div>
                <Label className="text-slate-900">Date sent</Label>
                <Input type="date" value={dateSent} onChange={(e) => setDateSent(e.target.value)}
                  className="mt-1 bg-white text-slate-900 border-slate-300" data-testid="proof-date" />
              </div>
              <div>
                <Label className="text-slate-900">Sending bank / wallet</Label>
                <Input value={source} onChange={(e) => setSource(e.target.value)}
                  className="mt-1 bg-white text-slate-900 border-slate-300" data-testid="proof-source" />
              </div>
            </div>
            <Button
              onClick={upload}
              disabled={submitting || (!file && !txHash)}
              className="bg-slate-900 text-white hover:bg-slate-800"
              data-testid="proof-submit"
            >
              {submitting ? "Uploading..." : "Submit proof of payment"}
            </Button>
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default PartnerFundingPage;
